import { Box, Breadcrumbs, Button, IconButton, Stack, Typography } from "@mui/material"
import { If } from "lib/@components/switch"
import { Else } from "lib/@components/else"
import Iconify from "minimals-template/components/Iconify"
import { MacroCategoryChips } from "./macro-category-chips"
import { MacroGuidanceList } from "./macro-guidance-list"
import { ComponentTokenInfo } from "./component-token-info"

export function MacroGuidanceDetail({
    selectedMacro,
    linkedMacros = [],
    breadcrumbItems = [],
    onBack,
    onOpenLinked,
    onBreadcrumbClick,
    maxHeight = 345,
}) {
    const breadcrumbContent = buildBreadcrumbContent(breadcrumbItems, onBreadcrumbClick)
    const selectedMacroName = selectedMacro?.name ?? selectedMacro?.label ?? "Macro"
    const selectedMacroId = selectedMacro?.id ?? ""
    const selectedMacroContent = selectedMacro?.content ?? ""
    const selectedMacroCategories = selectedMacro?.categories ?? []
    const isComponentToken = selectedMacroId.startsWith("cmp:")
    const isBroken = !!selectedMacro?.broken

    return (
        <If truthy={!!selectedMacro}>
            <Stack spacing={1} sx={{ maxHeight, overflowY: "auto" }}>
                <Stack direction="row" spacing={1} alignItems="center">
                    <IconButton size="small" onClick={handleBack} aria-label="Back to list">
                        <Iconify icon="mdi:chevron-left" />
                    </IconButton>
                    <Typography
                        variant="subtitle2"
                        data-cy="macro-guidance-detail-name"
                        sx={{ minWidth: 0, color: isBroken ? "error.main" : undefined }}
                    >
                        {selectedMacroName}
                    </Typography>
                </Stack>

                <If truthy={isComponentToken}>
                    <ComponentTokenInfo tokenId={selectedMacroId} isBroken={isBroken} />
                </If>

                <If truthy={breadcrumbContent.length > 0}>
                    <Breadcrumbs aria-label="macro breadcrumb">{breadcrumbContent}</Breadcrumbs>
                </If>

                <If truthy={!!selectedMacroContent}>
                    <Typography variant="caption" color="text.secondary">
                        Content
                    </Typography>
                    <Box
                        sx={{
                            px: 0.75,
                            py: 0.75,
                            borderRadius: 1,
                            border: "1px solid",
                            borderColor: isBroken ? "error.light" : "divider",
                            bgcolor: isBroken ? "error.lighter" : "background.neutral",
                        }}
                    >
                        <Box
                            className="styled-bullets"
                            data-cy="macro-guidance-detail-content"
                            sx={{
                                typography: "body2",
                                "& p": { mt: 0, mb: 1 },
                                "& p:last-of-type": { mb: 0 },
                            }}
                            dangerouslySetInnerHTML={{ __html: selectedMacroContent }}
                        />
                    </Box>
                </If>

                <If truthy={!isComponentToken}>
                    <Stack spacing={0.75}>
                        <Typography variant="caption" color="text.secondary">
                            Categories
                        </Typography>
                        <MacroCategoryChips categories={selectedMacroCategories} emptyMessage="No categories." />
                    </Stack>

                    <Stack spacing={0.75}>
                        <Typography variant="caption" color="text.secondary">
                            Linked macros
                        </Typography>
                        <MacroGuidanceList
                            macros={linkedMacros}
                            selectedId={selectedMacroId}
                            onOpen={onOpenLinked}
                            emptyMessage="No linked macros."
                            maxHeight={220}
                        />
                    </Stack>
                </If>
            </Stack>
            <Else>
                <Stack sx={{ minHeight: maxHeight, justifyContent: "center", alignItems: "center" }}>
                    <Typography variant="body2" color="text.secondary" align="center">
                        Click the chevron on the right of a macro to view content, linked macros, and categories.
                    </Typography>
                </Stack>
            </Else>
        </If>
    )

    function handleBack() {
        if (!onBack) return
        onBack()
    }
}

function buildBreadcrumbContent(items, onBreadcrumbClick) {
    if (!items?.length || items.length < 2) return []
    const breadcrumbs = []
    for (const item of items) {
        if (item?.isLast) {
            breadcrumbs.push(
                <Typography key={item.id} color="text.primary" variant="caption">
                    {item.label}
                </Typography>
            )
            continue
        }

        breadcrumbs.push(
            <Button
                key={item.id}
                size="small"
                onClick={() => onBreadcrumbClick?.(item.id)}
                sx={{ px: 0.5, minWidth: 0 }}
            >
                {item.label}
            </Button>
        )
    }
    return breadcrumbs
}
